import type { RouteConfig } from '../types';

export const routes: RouteConfig[] = [
  {
    path: '/',
    meta: {
      title: 'Fix Phone Speakers - Professional Audio Testing Tools',
      description: 'Free online tools to fix water-damaged speakers, test audio quality, and enhance sound. Professional-grade audio utilities for device maintenance and repair.',
      keywords: 'speaker fix, water damage, audio repair, sound test, speaker test',
      canonicalUrl: 'https://fixphonespeakers.com',
      structuredData: {
        "@context": "https://schema.org",
        "@type": "WebSite",
        "name": "Fix Phone Speakers",
        "url": "https://fixphonespeakers.com",
        "description": "Professional audio testing and repair tools"
      }
    }
  },
  {
    path: '/speaker-frequency-test',
    meta: {
      title: 'Speaker Frequency Test | Test Your Speakers Online',
      description: 'Test your speakers with our online frequency response tool. Generate test tones from 20Hz to 20kHz to verify speaker performance and identify frequency response issues.',
      keywords: 'speaker test, frequency response test, audio testing, sound frequency test',
      canonicalUrl: 'https://fixphonespeakers.com/speaker-frequency-test',
      structuredData: {
        "@context": "https://schema.org",
        "@type": "WebApplication",
        "name": "Speaker Frequency Test",
        "applicationCategory": "MultimediaApplication",
        "description": "Professional speaker frequency testing tool",
        "offers": {
          "@type": "Offer",
          "price": "0"
        }
      }
    }
  },
  {
    path: '/mp3-volume-booster',
    meta: {
      title: 'Online MP3 Volume Booster | Increase Audio Volume',
      description: 'Boost your MP3 audio volume online without quality loss. Simple and free tool to amplify quiet audio files and normalize sound levels.',
      keywords: 'volume booster, mp3 amplifier, audio volume increase, sound amplification',
      canonicalUrl: 'https://fixphonespeakers.com/mp3-volume-booster',
      structuredData: {
        "@context": "https://schema.org",
        "@type": "WebApplication",
        "name": "MP3 Volume Booster",
        "applicationCategory": "MultimediaApplication",
        "description": "Professional audio volume enhancement tool",
        "offers": {
          "@type": "Offer",
          "price": "0"
        }
      }
    }
  },
  {
    path: '/microphone-tester',
    meta: {
      title: 'Online Microphone Test | Check Your Mic Quality',
      description: 'Test your microphone quality with our free online tool. Real-time audio visualization, volume monitoring, and instant feedback for mic diagnostics.',
      keywords: 'microphone test, mic check, audio input test, voice recording test',
      canonicalUrl: 'https://fixphonespeakers.com/microphone-tester',
      structuredData: {
        "@context": "https://schema.org",
        "@type": "WebApplication",
        "name": "Microphone Tester",
        "applicationCategory": "MultimediaApplication",
        "description": "Professional microphone testing utility",
        "offers": {
          "@type": "Offer",
          "price": "0"
        }
      }
    }
  },
  {
    path: '/headphones-earphones-test',
    meta: {
      title: 'Headphones & Earphones Test | Audio Quality Check',
      description: 'Comprehensive headphone testing suite. Check audio quality, channel balance, and frequency response of your headphones or earphones.',
      keywords: 'headphone test, earphone test, audio quality test, stereo test',
      canonicalUrl: 'https://fixphonespeakers.com/headphones-earphones-test',
      structuredData: {
        "@context": "https://schema.org",
        "@type": "WebApplication",
        "name": "Headphones & Earphones Test",
        "applicationCategory": "MultimediaApplication",
        "description": "Professional headphone testing utility",
        "offers": {
          "@type": "Offer",
          "price": "0"
        }
      }
    }
  },
  {
    path: '/white-noise-and-pink-noise-generators',
    meta: {
      title: 'White Noise & Pink Noise Generator | Online Sound Generator',
      description: 'Generate high-quality white noise, pink noise, and brown noise online. Perfect for sleep, focus, and sound masking.',
      keywords: 'white noise, pink noise, brown noise, noise generator',
      canonicalUrl: 'https://fixphonespeakers.com/white-noise-and-pink-noise-generators',
      structuredData: {
        "@context": "https://schema.org",
        "@type": "WebApplication",
        "name": "Noise Generator",
        "applicationCategory": "MultimediaApplication",
        "description": "Professional noise generation tool",
        "offers": {
          "@type": "Offer",
          "price": "0"
        }
      }
    }
  },
  {
    path: '/equalizer-settings-online',
    meta: {
      title: 'Online Equalizer Settings | Audio EQ Tool',
      description: 'Professional-grade online equalizer with real-time preview. Adjust frequency bands, save presets, and enhance your audio quality.',
      keywords: 'equalizer, audio eq, sound equalizer, frequency adjustment',
      canonicalUrl: 'https://fixphonespeakers.com/equalizer-settings-online',
      structuredData: {
        "@context": "https://schema.org",
        "@type": "WebApplication",
        "name": "Online Equalizer",
        "applicationCategory": "MultimediaApplication",
        "description": "Professional audio equalization tool",
        "offers": {
          "@type": "Offer",
          "price": "0"
        }
      }
    }
  },
  {
    path: '/feedback-analyzer',
    meta: {
      title: 'Audio Feedback Analyzer | Online Sound Analysis',
      description: 'Analyze audio feedback and sound quality in real-time. Professional tool for identifying acoustic issues and optimizing sound systems.',
      keywords: 'feedback analyzer, audio analysis, sound quality test, acoustic analysis',
      canonicalUrl: 'https://fixphonespeakers.com/feedback-analyzer',
      structuredData: {
        "@context": "https://schema.org",
        "@type": "WebApplication",
        "name": "Feedback Analyzer",
        "applicationCategory": "MultimediaApplication",
        "description": "Professional audio feedback analysis tool",
        "offers": {
          "@type": "Offer",
          "price": "0"
        }
      }
    }
  },
  {
    path: '/voice-changer',
    meta: {
      title: 'Online Voice Changer | Free Voice Effects',
      description: 'Transform your voice with professional audio effects. Real-time voice modulation with multiple effects and instant preview.',
      keywords: 'voice changer, voice effects, audio effects, voice modulation',
      canonicalUrl: 'https://fixphonespeakers.com/voice-changer',
      structuredData: {
        "@context": "https://schema.org",
        "@type": "WebApplication",
        "name": "Voice Changer",
        "applicationCategory": "MultimediaApplication",
        "description": "Professional voice modulation tool",
        "offers": {
          "@type": "Offer",
          "price": "0"
        }
      }
    }
  },
  {
    path: '/left-right-stereo-sound-test',
    meta: {
      title: 'Left/Right Speaker Test | Stereo Balance Check',
      description: 'Test your stereo sound system with our left/right channel checker. Verify speaker balance and identify audio channel issues.',
      keywords: 'stereo test, left right test, channel balance, speaker test',
      canonicalUrl: 'https://fixphonespeakers.com/left-right-stereo-sound-test',
      structuredData: {
        "@context": "https://schema.org",
        "@type": "WebApplication",
        "name": "Stereo Sound Test",
        "applicationCategory": "MultimediaApplication",
        "description": "Professional stereo audio testing tool",
        "offers": {
          "@type": "Offer",
          "price": "0"
        }
      }
    }
  }
];